import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserPlus, KeyRound, ShieldCheck, Copy, Check } from 'lucide-react';
import { SUB_ROLES, type SubRole } from '@mdu-invoicing/shared';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, ApiError } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';

interface ManagedUser {
  id: string;
  email: string;
  displayName: string;
  subRole: SubRole | null;
  isActive: boolean;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  contractorId: string | null;
  contractorName: string | null;
  contractorCode: string | null;
}

interface ContractorOption {
  id: string;
  name: string;
  code: string;
}

/** 'YYYY-MM-DD'(T…) → 'DD/MM/YYYY' (Europe/Brussels); '—' when absent. */
function frDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('fr-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Reveal-once temp password (after a create / reset) — shown in a modal. */
interface TempPwd {
  email: string;
  password: string;
  reset: boolean;
}

export function Users() {
  const { user, isStaff } = useAuth();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // STAFF only: the contractors it may create in + the list filter.
  const [contractors, setContractors] = useState<ContractorOption[]>([]);
  const [contractorFilter, setContractorFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<{ email: string; displayName: string; subRole: SubRole; contractorId: string }>({
    email: '',
    displayName: '',
    subRole: SUB_ROLES.TECH,
    contractorId: '',
  });
  const [tempPwd, setTempPwd] = useState<TempPwd | null>(null);

  // Contractors the caller can pick (create dropdown + STAFF filter) — fetched once.
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ contractors: ContractorOption[] }>('/users/meta')
      .then((d) => !cancelled && setContractors(d.contractors))
      .catch(() => {
        /* meta is best-effort — the list still loads; a create just can't pick a contractor */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The user list, refetched when the STAFF contractor filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = isStaff && contractorFilter ? `?contractorId=${encodeURIComponent(contractorFilter)}` : '';
    apiFetch<{ users: ManagedUser[] }>(`/users${qs}`)
      .then((d) => {
        if (cancelled) return;
        setUsers(d.users);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, contractorFilter]);

  function replaceUser(u: ManagedUser) {
    setUsers((list) => (list ? list.map((x) => (x.id === u.id ? u : x)) : list));
  }

  async function changeSubRole(u: ManagedUser, subRole: SubRole) {
    if (subRole === u.subRole) return;
    setBusyId(u.id);
    try {
      const { user: updated } = await apiFetch<{ user: ManagedUser }>(`/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ subRole }),
      });
      replaceUser(updated);
      toast.success(`${updated.displayName} → ${subRole === SUB_ROLES.ADMIN ? 'Administrateur' : 'Technicien'}`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la modification');
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(u: ManagedUser) {
    setBusyId(u.id);
    try {
      const { user: updated } = await apiFetch<{ user: ManagedUser }>(`/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !u.isActive }),
      });
      replaceUser(updated);
      toast.success(updated.isActive ? 'Compte activé' : 'Compte désactivé');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la modification');
    } finally {
      setBusyId(null);
    }
  }

  async function resetPassword(u: ManagedUser) {
    setBusyId(u.id);
    try {
      const { tempPassword } = await apiFetch<{ tempPassword: string }>(`/users/${u.id}/reset-password`, {
        method: 'POST',
      });
      setTempPwd({ email: u.email, password: tempPassword, reset: true });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la réinitialisation');
    } finally {
      setBusyId(null);
    }
  }

  function closeCreate() {
    setCreateOpen(false);
    setForm({ email: '', displayName: '', subRole: SUB_ROLES.TECH, contractorId: '' });
  }

  async function submitCreate() {
    setCreating(true);
    try {
      const body = {
        email: form.email,
        displayName: form.displayName,
        subRole: form.subRole,
        ...(isStaff ? { contractorId: form.contractorId } : {}),
      };
      const { user: created, tempPassword } = await apiFetch<{ user: ManagedUser; tempPassword: string }>('/users', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      // Only surface the new row if it matches the active list filter — STAFF may create
      // in a different company than the one currently filtered (it will appear on refetch).
      setUsers((list) => {
        if (!list) return [created];
        if (isStaff && contractorFilter && created.contractorId !== contractorFilter) return list;
        return [created, ...list];
      });
      closeCreate();
      setTempPwd({ email: created.email, password: tempPassword, reset: false });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la création');
    } finally {
      setCreating(false);
    }
  }

  // STAFF create needs a contractor chosen; both need email + name.
  const createDisabled =
    creating || form.email.trim() === '' || form.displayName.trim() === '' || (isStaff && form.contractorId === '');
  const colCount = 7 + (isStaff ? 1 : 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 text-sm text-slate-500">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-purple" />
          <p>
            {isStaff ? 'Gérez les comptes des sociétés partenaires. ' : 'Gérez les comptes de votre sous-traitance. '}
            Un <span className="font-semibold">Administrateur</span> voit les prix et gère les comptes ; un{' '}
            <span className="font-semibold">Technicien</span> non.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 rounded-lg bg-brand-gradient px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
        >
          <UserPlus className="h-4 w-4" />
          Nouveau compte
        </button>
      </div>

      {/* STAFF: filter by contractor */}
      {isStaff && (
        <div className="flex items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Société</span>
            <select
              value={contractorFilter}
              onChange={(e) => setContractorFilter(e.target.value)}
              aria-label="Filtrer par société"
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
            >
              <option value="">Toutes</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {loading ? (
        <Card className="flex items-center gap-2 p-5 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement des comptes…
        </Card>
      ) : error ? (
        <Card className="p-5 text-sm text-rose-700">Impossible de charger les comptes : {error}.</Card>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {isStaff && <th className="px-4 py-3">Société</th>}
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">E-mail</th>
                  <th className="px-4 py-3">Rôle</th>
                  <th className="px-4 py-3">2FA</th>
                  <th className="px-4 py-3">Dernière connexion</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(users ?? []).map((u) => {
                  const isSelf = u.id === user?.id;
                  const rowBusy = busyId === u.id;
                  return (
                    <tr key={u.id} className={cn('hover:bg-slate-50/60', !u.isActive && 'opacity-60')}>
                      {isStaff && (
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{u.contractorName ?? '—'}</td>
                      )}
                      <td className="whitespace-nowrap px-4 py-2.5 font-semibold text-slate-800">
                        {u.displayName}
                        {isSelf && <span className="ml-1.5 text-xs font-normal text-slate-400">(vous)</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <select
                          value={u.subRole ?? SUB_ROLES.TECH}
                          disabled={rowBusy || isSelf}
                          aria-label={`Rôle de ${u.displayName}`}
                          title={isSelf ? 'Vous ne pouvez pas changer votre propre rôle' : undefined}
                          onChange={(e) => void changeSubRole(u, e.target.value as SubRole)}
                          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20 disabled:opacity-50"
                        >
                          <option value={SUB_ROLES.ADMIN}>Administrateur</option>
                          <option value={SUB_ROLES.TECH}>Technicien</option>
                        </select>
                      </td>
                      <td className="px-4 py-2.5">
                        {u.totpEnabled ? (
                          <span className="text-xs font-medium text-emerald-600">Configurée</span>
                        ) : (
                          <span className="text-xs text-amber-600">En attente</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{frDate(u.lastLoginAt)}</td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            'inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-semibold',
                            u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500',
                          )}
                        >
                          {u.isActive ? 'Actif' : 'Désactivé'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            disabled={rowBusy}
                            onClick={() => void resetPassword(u)}
                            className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
                            title="Réinitialiser le mot de passe"
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                            Mot de passe
                          </button>
                          <button
                            type="button"
                            disabled={rowBusy || isSelf}
                            onClick={() => void toggleActive(u)}
                            aria-label={u.isActive ? `Désactiver ${u.displayName}` : `Activer ${u.displayName}`}
                            aria-busy={rowBusy}
                            title={isSelf ? 'Vous ne pouvez pas désactiver votre propre compte' : undefined}
                            className={cn(
                              'rounded-md border px-2 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                              u.isActive
                                ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                            )}
                          >
                            {rowBusy ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : u.isActive ? (
                              'Désactiver'
                            ) : (
                              'Activer'
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(users ?? []).length === 0 && (
                  <tr>
                    <td colSpan={colCount} className="px-4 py-12 text-center text-sm text-slate-400">
                      Aucun compte. Créez le premier avec « Nouveau compte ».
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create-account modal */}
      <Modal open={createOpen} onClose={closeCreate} dismissable={!creating} title="Nouveau compte">
        <div className="space-y-4">
          {isStaff && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Société</span>
              <select
                value={form.contractorId}
                onChange={(e) => setForm((f) => ({ ...f, contractorId: e.target.value }))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
              >
                <option value="">— Choisir une société —</option>
                {contractors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {contractors.length === 0 && (
                <span className="text-xs text-amber-600">Liste des sociétés indisponible — réessayez plus tard.</span>
              )}
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Nom</span>
            <input
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="Prénom Nom"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">E-mail</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="prenom.nom@exemple.be"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rôle</span>
            <select
              value={form.subRole}
              onChange={(e) => setForm((f) => ({ ...f, subRole: e.target.value as SubRole }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple/20"
            >
              <option value={SUB_ROLES.TECH}>Technicien (sans prix, sans gestion)</option>
              <option value={SUB_ROLES.ADMIN}>Administrateur (prix + gestion des comptes)</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={closeCreate}
              disabled={creating}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => void submitCreate()}
              disabled={createDisabled}
              className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Créer le compte
            </button>
          </div>
        </div>
      </Modal>

      {/* Reveal-once temp password */}
      <TempPasswordModal info={tempPwd} onClose={() => setTempPwd(null)} />
    </div>
  );
}

/** One-time temp-password reveal after a create / reset (copyable; shown once). */
function TempPasswordModal({ info, onClose }: { info: TempPwd | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  // Reset the "Copié" flash between reveals (this modal stays mounted across opens).
  useEffect(() => setCopied(false), [info]);
  async function copy() {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copie impossible — sélectionnez le mot de passe manuellement');
    }
  }
  return (
    <Modal
      open={info !== null}
      onClose={onClose}
      dismissable={false}
      title={info?.reset ? 'Mot de passe réinitialisé' : 'Compte créé'}
    >
      {info && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Mot de passe temporaire pour <span className="font-semibold text-slate-700">{info.email}</span>. Communiquez-le
            de façon sécurisée — <span className="font-semibold">il ne sera plus affiché</span>. L'utilisateur configurera
            sa 2FA à la première connexion.
          </p>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <code className="select-all font-mono text-sm font-semibold text-slate-800">{info.password}</code>
            <button
              type="button"
              onClick={() => void copy()}
              className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copié' : 'Copier'}
            </button>
          </div>
          <div className="flex justify-end border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              J'ai noté le mot de passe
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
